// ABOUTME: SPEC-67 D-9 — the pure half of the reconnect handshake: is this open the same page's
// ABOUTME: reconnect, how long to wait for the backend's `session_resume`, and how to read that frame.
//
// WHY (incident 2026-09-25, ophion prod): every reconnect reset the Vélos stage to the reel with no
// fade and asked for a fresh welcome, because `ws.onopen` could not tell a dropped connection from a
// new visit (RC-2). Only a socket of THIS page that opened before, on the SAME session id, is a
// candidate for resume; everything else — the first open, a new sid after force_reconnect, a
// re-login after a 1008 — takes today's path unchanged.

/** OQ-4 (a): bounded so an old backend that never sends the frame costs 2.5 s, then today's path. */
export const RESUME_ANSWER_TIMEOUT_MS = 2500;

/**
 * Mirrors main.py's waiting-room sleep: a queued connect sends WAITING_ROOM, then sleeps 3 s. Once a
 * WAITING_ROOM frame has arrived, the wait must outlast that cadence, or the fallback fires between
 * two frames and the guest drops to the reel while the backend later resumes onto the Vélos stage
 * (backend review, 2026-09-25).
 */
export const WAITING_ROOM_INTERVAL_MS = 3000;

/** D-11: the backend closes with this after a first context overflow — retried on the same sid. */
export const RETRY_SAME_SESSION_CLOSE = 4007;

/** 1008 ends the experience (auth); the next open must be a fresh visit. */
export const AUTH_CLOSE = 1008;

export const OpenKind = Object.freeze({
  FIRST: 'first',             // no socket of this page has opened yet
  SAME_PAGE: 'same_page',     // this page opened a socket before, on this very sid
  NEW_SESSION: 'new_session', // this page opened before, but the sid changed (force_reconnect)
});

/** Classify an open. Pure. @returns {'first'|'same_page'|'new_session'} */
export function classifyOpen({ openedSid, sid } = {}) {
  if (!openedSid || !sid) return OpenKind.FIRST;
  return openedSid === sid ? OpenKind.SAME_PAGE : OpenKind.NEW_SESSION;
}

/** `?resume=1` rides only on a same-page reconnect (D-9). Pure. */
export function wantsResumeParam(kind) {
  return kind === OpenKind.SAME_PAGE;
}

/**
 * Read a `session_resume` frame at the boundary. Anything malformed is `resumed:false` (§4.5), so a
 * bad frame can only ever cost today's path, never a kept-but-wrong screen. Pure.
 * @returns {{ resumed: boolean, reason: string, stage: object|null }}
 */
export function parseResumeAnswer(msg) {
  const m = msg && typeof msg === 'object' ? msg : {};
  const stage = m.stage && typeof m.stage === 'object' && !Array.isArray(m.stage) ? m.stage : null;
  return {
    resumed: m.resumed === true,
    reason: typeof m.reason === 'string' ? m.reason : 'malformed',
    stage,
  };
}

/**
 * The backend's waiting-room frame (a `ui_update` naming WAITING_ROOM): the connect is queued, so
 * the resume answer is legitimately late and the timer restarts. Matched on `view` or `action`,
 * case-insensitively — see the SPEC-67 build note on this shape. Pure.
 */
export function isWaitingRoomFrame(msg) {
  if (!msg || msg.type !== 'ui_update') return false;
  const named = (v) => typeof v === 'string' && v.toUpperCase() === 'WAITING_ROOM';
  return named(msg.view) || named(msg.action);
}
